<script setup>
import { format } from '@formkit/tempo'
import Tag from '@components/global/Tag.vue'
import Button from '@components/forms/Button.vue'
import EventCardTags from '@components/index/EventCardTags.vue'

const props = defineProps({
	event: {
		type: Object,
		required: true,
	},
	compact: {
		type: Boolean,
		default: false,
	},
	light: {
		type: Boolean,
		default: false,
	},
	card: {
		type: Boolean,
		default: false,
	},
})

const dateRange = (start, end) => {
	const startMonth = format({ date: start, format: 'MMM', tz: 'UTC' })
	const endMonth = format({ date: end, format: 'MMM', tz: 'UTC' })
	const startDay = format({ date: start, format: 'D', tz: 'UTC' })
	const endDay = format({ date: end, format: 'D', tz: 'UTC' })

	if (startMonth === endMonth && startDay === endDay) {
		return `${startMonth}. ${startDay}`
	} else if (startMonth === endMonth) {
		return `${startMonth}. ${startDay}-${endDay}`
	} else {
		return `${startMonth}. ${startDay} - ${endMonth}. ${endDay}`
	}
}
</script>

<template>
	<div class="flex flex-col justify-between gap-5 rounded-xl bg-white p-5 lg:flex-row">
		<div class="flex gap-5">
			<div class="flex size-16 items-center justify-center overflow-hidden rounded-xl bg-snk-blue-500 p-1" v-if="event.logo">
				<img class="h-full w-full object-contain" :src="event.logo" :alt="event.name" />
			</div>
			<div class="flex flex-col justify-center gap-2">
				<div class="flex flex-col items-start md:flex-row md:items-center md:gap-2">
					<div class="text-lg font-bold">{{ event.name }}</div>
					<div class="text-nowrap rounded-[.25rem] bg-[#EE7D00] p-1 text-[.6rem] font-bold uppercase leading-none text-white" v-if="event.ewc_slots">EWC {{ event.ewc_slots }} Slots</div>
				</div>
				<p class="text-sm text-neutral-400">
					{{ dateRange(event.start_at, event.finish_at) }}
					<span v-if="event.country?.name">, {{ event.country?.name }}</span>
				</p>
			</div>
		</div>
		<div class="flex flex-col gap-2 md:flex-row md:gap-5">
			<div class="flex flex-col gap-1">
				<div class="text-xs font-bold uppercase text-neutral-400">Fatal Fury: COTW</div>
				<a
					:class="['flex h-8 w-full items-center justify-center rounded-lg bg-snk-light-500 text-xs font-bold uppercase text-white transition-transform duration-300 ease-in-out md:h-10 md:w-40 md:hover:scale-105', event.external_links.find((link) => link.game.slug == 'fatal-fury')?.accent_color == 'primary' ? 'bg-snk-blue-500!' : event.external_links.find((link) => link.game.slug == 'fatal-fury')?.accent_color == 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!']"
					:href="event.external_links.find((link) => link.game.slug == 'fatal-fury')?.url"
					v-if="event.external_links.find((link) => link.game.slug == 'fatal-fury')?.url"
					target="_blank">
					{{ event.external_links.find((link) => link.game.slug == 'fatal-fury')?.label }}
				</a>
				<div :class="['pointer-events-none flex h-8 w-full items-center justify-center rounded-lg bg-snk-light-500 text-xs font-bold uppercase text-white transition-transform duration-300 ease-in-out md:h-10 md:w-40', event.external_links.find((link) => link.game.slug == 'fatal-fury')?.accent_color == 'primary' ? 'bg-snk-blue-500!' : event.external_links.find((link) => link.game.slug == 'fatal-fury')?.accent_color == 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!']" v-else-if="event.external_links.find((link) => link.game.slug == 'fatal-fury')?.label">
					{{ event.external_links.find((link) => link.game.slug == 'fatal-fury')?.label }}
				</div>
				<div class="h-10 w-full rounded-lg bg-snk-light-500 md:w-40" v-else />
			</div>
			<div class="flex flex-col gap-1">
				<div class="text-xs font-bold uppercase text-neutral-400">KOF XV</div>
				<a
					:class="['flex h-8 w-full items-center justify-center rounded-lg bg-snk-light-500 text-xs font-bold uppercase text-white transition-transform duration-300 ease-in-out md:h-10 md:w-40 md:hover:scale-105', event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.accent_color == 'primary' ? 'bg-snk-blue-500!' : event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.accent_color == 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!']"
					:href="event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.url"
					v-if="event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.url"
					target="_blank">
					{{ event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.label }}
				</a>
				<div :class="['pointer-events-none flex h-8 w-full items-center justify-center rounded-lg bg-snk-light-500 text-xs font-bold uppercase text-white transition-transform duration-300 ease-in-out md:h-10 md:w-40', event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.accent_color == 'primary' ? 'bg-snk-blue-500!' : event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.accent_color == 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!']" v-else-if="event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.label">
					{{ event.external_links.find((link) => link.game.slug == 'king-of-fighters')?.label }}
				</div>
				<div class="h-10 w-full rounded-lg bg-snk-light-500 md:w-40" v-else />
			</div>
			<div class="flex flex-col gap-1">
				<div class="text-xs font-bold uppercase text-neutral-400">Samurai Shodown</div>
				<a
					:class="['flex h-8 w-full items-center justify-center rounded-lg bg-snk-light-500 text-xs font-bold uppercase text-white transition-transform duration-300 ease-in-out md:h-10 md:w-40 md:hover:scale-105', event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.accent_color == 'primary' ? 'bg-snk-blue-500!' : event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.accent_color == 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!']"
					:href="event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.url"
					v-if="event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.url"
					target="_blank">
					{{ event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.label }}
				</a>
				<div :class="['pointer-events-none flex h-8 w-full items-center justify-center rounded-lg bg-snk-light-500 text-xs font-bold uppercase text-white transition-transform duration-300 ease-in-out md:h-10 md:w-40', event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.accent_color == 'primary' ? 'bg-snk-blue-500!' : event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.accent_color == 'gold' ? 'bg-[#EB7C08]!' : 'bg-[#6A6A6A]!']" v-else-if="event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.label">
					{{ event.external_links.find((link) => link.game.slug == 'samurai-shodown')?.label }}
				</div>
				<div class="h-10 w-full rounded-lg bg-snk-light-500 md:w-40" v-else />
			</div>
		</div>
	</div>
</template>
